import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Loader2, Layers, ChevronDown, ChevronUp, Info, Sparkles } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { authFetch, handleResponse } from '@/utils/auth-fetch';

export interface SectionPreset {
  id: string;
  name: string;
  description: string;
  sections: string[];
  sectionCount: number;
}

export interface Section {
  id: string;
  label: string;
  group: string;
}

export interface SectionGroup {
  id: string;
  label: string;
}

interface SectionSelectorProps {
  selectedSections: string[];
  onSectionsChange: (sections: string[]) => void;
  selectedPreset: string;
  onPresetChange: (preset: string) => void;
  disabled?: boolean;
}

export function SectionSelector({
  selectedSections,
  onSectionsChange,
  selectedPreset,
  onPresetChange,
  disabled = false,
}: SectionSelectorProps) {
  const [presets, setPresets] = useState<SectionPreset[]>([]);
  const [sections, setSections] = useState<Section[]>([]);
  const [groups, setGroups] = useState<SectionGroup[]>([]);
  const [totalSections, setTotalSections] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCustom, setShowCustom] = useState(false);

  useEffect(() => {
    loadPresets();
  }, []);

  useEffect(() => {
    setShowCustom(selectedPreset === 'custom');
  }, [selectedPreset]);

  const loadPresets = async () => {
    try {
      const response = await authFetch('/api/aeo-benchmark/section-presets');
      const data = await handleResponse<{
        presets: SectionPreset[];
        sections: Section[];
        groups: SectionGroup[];
        totalSections: number;
      }>(response);

      setPresets(data.presets);
      setSections(data.sections);
      setGroups(data.groups);
      setTotalSections(data.totalSections);
    } catch (err: any) {
      console.error('Failed to load presets:', err);
      setError(err.message || 'Failed to load section presets');
    } finally {
      setLoading(false);
    }
  };

  const handlePresetSelect = (presetId: string) => {
    onPresetChange(presetId);

    if (presetId === 'custom') {
      setShowCustom(true);
      return;
    }

    const preset = presets.find(p => p.id === presetId);
    if (preset) {
      onSectionsChange(preset.sections);
      setShowCustom(false);
    }
  };

  const handleSectionToggle = (sectionId: string, checked: boolean) => {
    if (checked) {
      onSectionsChange([...selectedSections, sectionId]);
    } else {
      onSectionsChange(selectedSections.filter(s => s !== sectionId));
    }
    // If manually toggling sections, switch to custom preset
    if (selectedPreset !== 'custom') {
      onPresetChange('custom');
    }
  };

  const handleGroupToggle = (groupId: string, checked: boolean) => {
    const groupSections = sections.filter(s => s.group === groupId).map(s => s.id);

    if (checked) {
      const newSections = [...new Set([...selectedSections, ...groupSections])];
      onSectionsChange(newSections);
    } else {
      onSectionsChange(selectedSections.filter(s => !groupSections.includes(s)));
    }

    if (selectedPreset !== 'custom') {
      onPresetChange('custom');
    }
  };

  const isGroupFullySelected = (groupId: string) => {
    const groupSections = sections.filter(s => s.group === groupId);
    return groupSections.every(s => selectedSections.includes(s.id));
  };

  const isGroupPartiallySelected = (groupId: string) => {
    const groupSections = sections.filter(s => s.group === groupId);
    const selectedCount = groupSections.filter(s => selectedSections.includes(s.id)).length;
    return selectedCount > 0 && selectedCount < groupSections.length;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading section options...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive text-sm py-4">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Preset Selection */}
      <div className="space-y-3">
        <Label className="text-base font-medium">AEO Section Coverage</Label>
        <RadioGroup
          value={selectedPreset}
          onValueChange={handlePresetSelect}
          className="grid gap-3"
          disabled={disabled}
        >
          {presets.filter(p => p.id !== 'custom').map(preset => (
            <div key={preset.id} className="flex items-start space-x-3">
              <RadioGroupItem value={preset.id} id={preset.id} className="mt-1" />
              <Label
                htmlFor={preset.id}
                className="flex-1 cursor-pointer space-y-1"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium">{preset.name}</span>
                  <Badge variant="secondary" className="text-xs">
                    {preset.sectionCount} section{preset.sectionCount !== 1 ? 's' : ''}
                  </Badge>
                  {preset.id === 'full' && (
                    <Badge variant="default" className="text-xs">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Recommended
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-muted-foreground font-normal">
                  {preset.description}
                </p>
              </Label>
            </div>
          ))}

          {/* Custom option */}
          <div className="flex items-start space-x-3">
            <RadioGroupItem value="custom" id="custom" className="mt-1" />
            <Label
              htmlFor="custom"
              className="flex-1 cursor-pointer space-y-1"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium">Custom</span>
                {selectedPreset === 'custom' && (
                  <Badge variant="outline" className="text-xs">
                    {selectedSections.length} of {totalSections} selected
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground font-normal">
                Select individual sections
              </p>
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Custom Section Selection (expandable) */}
      {showCustom && (
        <Card className="border-dashed">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Custom Section Selection
            </CardTitle>
            <CardDescription>
              Select which AEO sections to include in the benchmark
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Accordion type="multiple" defaultValue={groups.map(g => g.id)} className="w-full">
              {groups.map(group => {
                const groupSections = sections.filter(s => s.group === group.id);
                const fullySelected = isGroupFullySelected(group.id);
                const partiallySelected = isGroupPartiallySelected(group.id);

                return (
                  <AccordionItem key={group.id} value={group.id}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 w-full pr-4">
                        <Checkbox
                          checked={fullySelected}
                          className={partiallySelected ? 'data-[state=checked]:bg-primary/50' : ''}
                          onCheckedChange={(checked) => {
                            handleGroupToggle(group.id, checked as boolean);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          disabled={disabled}
                        />
                        <span className="font-medium">{group.label}</span>
                        <Badge variant="outline" className="ml-auto text-xs">
                          {groupSections.filter(s => selectedSections.includes(s.id)).length}/{groupSections.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="ml-6 space-y-2 pt-2">
                        {groupSections.map(section => (
                          <div key={section.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={section.id}
                              checked={selectedSections.includes(section.id)}
                              onCheckedChange={(checked) => {
                                handleSectionToggle(section.id, checked as boolean);
                              }}
                              disabled={disabled}
                            />
                            <Label
                              htmlFor={section.id}
                              className="text-sm cursor-pointer"
                            >
                              {section.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>

            {/* Quick actions */}
            <div className="flex gap-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSectionsChange(sections.map(s => s.id))}
                disabled={disabled}
              >
                Select All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSectionsChange([])}
                disabled={disabled}
              >
                Clear All
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selection Summary */}
      {selectedSections.length > 0 && selectedSections.length < totalSections && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg text-sm">
          <Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" />
          <div className="text-blue-700 dark:text-blue-300">
            <strong>Partial AEO mode:</strong> Your benchmark will compare your current page enhanced with {selectedSections.length} AEO sections against competitors.
            This helps identify which sections provide the most value.
          </div>
        </div>
      )}
    </div>
  );
}
